import React, { useState, useEffect, useCallback } from 'react';
import './FeedEmptyState.css';

/**
 * FeedEmptyState — interactive bear mascot when feed is empty.
 * Eyes follow mouse cursor.
 */
export const FeedEmptyState = ({ language = 'he' }) => {
  const isHebrew = language === 'he';
  const [eyeOffset, setEyeOffset] = useState({ x: 0, y: 0 });

  const handleMouseMove = useCallback((e) => {
    const container = document.querySelector('.feed-empty-bear');
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const dx = e.clientX - centerX;
    const dy = e.clientY - centerY;
    const maxOffset = 3;
    const distance = Math.sqrt(dx * dx + dy * dy);
    const normalizedX = distance > 0 ? (dx / distance) * Math.min(distance / 100, 1) * maxOffset : 0;
    const normalizedY = distance > 0 ? (dy / distance) * Math.min(distance / 100, 1) * maxOffset : 0;

    setEyeOffset({ x: normalizedX, y: normalizedY });
  }, []);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, [handleMouseMove]);

  return (
    <div className="feed-empty-state">
      <svg className="feed-empty-bear" viewBox="0 0 100 100" aria-hidden="true">
        {/* Head */}
        <ellipse cx="50" cy="52" rx="30" ry="28" fill="var(--neutral-200, #e5e7eb)" />
        {/* Ears */}
        <circle cx="26" cy="32" r="10" fill="var(--neutral-200, #e5e7eb)" />
        <circle cx="74" cy="32" r="10" fill="var(--neutral-200, #e5e7eb)" />
        <circle cx="26" cy="32" r="5" fill="var(--neutral-300, #d1d5db)" />
        <circle cx="74" cy="32" r="5" fill="var(--neutral-300, #d1d5db)" />
        {/* Eye sockets */}
        <ellipse cx="38" cy="50" rx="7" ry="5" fill="var(--neutral-100, #f3f4f6)" />
        <ellipse cx="62" cy="50" rx="7" ry="5" fill="var(--neutral-100, #f3f4f6)" />
        {/* Pupils — animated */}
        <circle cx={38 + eyeOffset.x} cy={50 + eyeOffset.y} r="3" fill="var(--neutral-700, #374151)" />
        <circle cx={62 + eyeOffset.x} cy={50 + eyeOffset.y} r="3" fill="var(--neutral-700, #374151)" />
        {/* Eye highlights */}
        <circle cx="39" cy="48" r="1" fill="white" opacity="0.8" />
        <circle cx="63" cy="48" r="1" fill="white" opacity="0.8" />
        {/* Snout */}
        <ellipse cx="50" cy="60" rx="10" ry="7" fill="var(--neutral-300, #d1d5db)" />
        <ellipse cx="50" cy="57" rx="4" ry="3" fill="var(--neutral-500, #6b7280)" />
        {/* Smile */}
        <path d="M 44 64 Q 50 68 56 64" fill="none" stroke="var(--neutral-500, #6b7280)" strokeWidth="1.5" strokeLinecap="round" />
        {/* Blush */}
        <ellipse cx="30" cy="55" rx="4" ry="2" fill="#fbcfe8" opacity="0.5" />
        <ellipse cx="70" cy="55" rx="4" ry="2" fill="#fbcfe8" opacity="0.5" />
      </svg>

      <div className="feed-empty-message">
        <h4>{isHebrew ? '\u05E9\u05E7\u05D8 \u05E4\u05D4...' : "It's quiet here..."}</h4>
        <p>
          {isHebrew
            ? '\u05D0\u05D9\u05DF \u05E4\u05E2\u05D9\u05DC\u05D5\u05EA \u05D7\u05D3\u05E9\u05D4 \u05DC\u05D4\u05E6\u05D9\u05D2. \u05D7\u05D6\u05D5\u05E8 \u05DE\u05D0\u05D5\u05D7\u05E8 \u05D9\u05D5\u05EA\u05E8!'
            : 'No new activity to show. Check back later!'}
        </p>
      </div>
    </div>
  );
};
